const STORAGE_KEY = "gca_lichess_pkce";
const AUTHORIZE_URL = "https://lichess.org/oauth";
const TOKEN_URL = "https://lichess.org/api/token";
const DEFAULT_SCOPE = "challenge:write";

function toBase64Url(bytes) {
  const base64 = btoa(String.fromCharCode(...bytes));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(byteLength = 32) {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
}

async function sha256(input) {
  const encoded = new TextEncoder().encode(input);
  const digest = await window.crypto.subtle.digest("SHA-256", encoded);
  return new Uint8Array(digest);
}

function getClientId() {
  return process.env.REACT_APP_LICHESS_CLIENT_ID || window.location.host;
}

function getRedirectUri() {
  return `${window.location.origin}/`;
}

export async function startLichessPkceLogin({ userId, platformAccountId }) {
  const state = randomString(24);
  const codeVerifier = randomString(48);
  const codeChallenge = toBase64Url(await sha256(codeVerifier));

  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      state,
      codeVerifier,
      userId,
      platformAccountId,
      createdAt: Date.now(),
    })
  );

  const params = new URLSearchParams({
    response_type: "code",
    client_id: getClientId(),
    redirect_uri: getRedirectUri(),
    code_challenge_method: "S256",
    code_challenge: codeChallenge,
    scope: DEFAULT_SCOPE,
    state,
  });

  window.location.assign(`${AUTHORIZE_URL}?${params.toString()}`);
}

export async function finishLichessPkceLogin() {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  const error = url.searchParams.get("error");

  if (!code && !error) return null;

  const storedRaw = localStorage.getItem(STORAGE_KEY);
  if (!storedRaw) {
    throw new Error("Missing saved Lichess login state.");
  }

  const stored = JSON.parse(storedRaw);

  if (error) {
    localStorage.removeItem(STORAGE_KEY);
    throw new Error(`Lichess denied the connection request: ${error}`);
  }

  if (stored.state !== state) {
    localStorage.removeItem(STORAGE_KEY);
    throw new Error("Lichess login state did not match.");
  }

  const response = await fetch(TOKEN_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: getRedirectUri(),
      client_id: getClientId(),
      code_verifier: stored.codeVerifier,
    }),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok || !payload.access_token) {
    localStorage.removeItem(STORAGE_KEY);
    throw new Error(payload?.error_description || payload?.error || "Failed to complete Lichess login.");
  }

  localStorage.removeItem(STORAGE_KEY);
  return {
    accessToken: payload.access_token,
    scope: payload.scope || DEFAULT_SCOPE,
    platformAccountId: stored.platformAccountId,
    userId: stored.userId,
  };
}

export function clearLichessPkceQuery() {
  window.history.replaceState({}, "", `${window.location.pathname}#account`);
}
